import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect } from 'react';

import { useLogout } from '@/modules/auth/apiHooks';
import { Button, Typography } from '@/shared/components/atoms';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import { AuthContext } from '@/shared/hooks/context/auth';
import {
  clearStoreData,
  useStore,
} from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import LogoutIcon from '~/images/shared/logout.svg';

const LogoutItem: React.FC<{ iconOnly?: boolean; color?: string }> = ({
  iconOnly,
  color = 'text-error-400',
}) => {
  const { mutate: mutateLogout, isSuccess } = useLogout();
  const { t: navigationBarT } = useTranslation('navigation');
  const { t: commonT } = useTranslation('common');

  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { logout } = useContext(AuthContext);
  const [_store, setStore] = useStore((state) => state);
  /**
   * logout functionality :
   *   1 clear local storage of generic context
   *   2 clear cach of react query
   *   3 clear state of genric context throw setStore(null)
   *   4 remove token and user from cookies (this happen in logout function in auth provider)
   */
  const handleLogout = async () => {
    mutateLogout(undefined, {
      onSuccess: () => {
        // note this is work arround should be removed
        // clear cach of asset
        queryClient.clear();
        // we clear storeData from localstorage and  generic context
        clearStoreData();
        setStore(null);
        logout();
        // close modal to remove body scroll lock when logging in again
        closeModal();
      },
    });
  };
  // when success logout clear client side data
  useEffect(() => {
    if (isSuccess) {
      // clear cach of asset
      queryClient.clear();
      // we clear storeData from localstorage and  generic context
      clearStoreData();
      setStore(null);

      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
  return (
    <div className='relative flex items-center justify-center'>
      <div className='w-full flex-col gap-16 p-2 transition-all'>
        <div
          className={classNames(
            'flex w-full cursor-pointer flex-nowrap items-center gap-2 text-24',
            color
          )}
          onClick={() => {
            openModal(
              <div className='flex h-full  w-full  flex-col gap-20 text-center'>
                {/* *********** icon ********** */}
                <div className='m-auto flex h-80 w-80 items-center justify-center rounded-full  bg-error-100'>
                  <BaseIcon
                    size='xl'
                    svgClasses='text-error-default ltr:rotate-180 '
                  >
                    <LogoutIcon />
                  </BaseIcon>
                </div>
                {/* *******confirm message ** */}
                <Typography variant='body1'>
                  {navigationBarT('are-you-sure-to-logout')}
                </Typography>
                {/* ******* actions ******* */}
                <div className='flex gap-32'>
                  <Button
                    onClick={handleLogout}
                    variant='filled'
                    color='primary'
                  >
                    {commonT('yes')}
                  </Button>
                  <Button
                    onClick={() => closeModal()}
                    color='secondary'
                    variant='filled'
                  >
                    {commonT('no')}
                  </Button>
                </div>
              </div>
            );
          }}
        >
          <LogoutIcon />
          {!iconOnly && (
            <Typography variant='subtitle2' weight='semiBold'>
              {navigationBarT('logout')}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};
export default LogoutItem;
